import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Web Speech API helper.
 *
 * Usage:
 *   const tts = useTTS();
 *   tts.speak([{ id, text }, ...]);
 *   tts.pause(); tts.resume(); tts.stop(); tts.restart();
 *   tts.setRate(1.5);
 *   tts.state // {playing, paused, currentId, rate}
 *
 * Behavior:
 *   - Cancels any in-flight speech when component unmounts (parar TTS ao trocar de tela)
 *   - Uses pt-BR voice when available; falls back to default voice
 *   - Persists preferred rate in cm_reading_settings_v1
 */
const SETTINGS_KEY = "cm_reading_settings_v1";
const ALLOWED_RATES = [0.75, 1, 1.25, 1.5, 2];
const DEFAULT_RATE = 1;

const readRate = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const r = Number(parsed?.ttsRate);
    return ALLOWED_RATES.includes(r) ? r : DEFAULT_RATE;
  } catch {
    return DEFAULT_RATE;
  }
};

const writeRate = (rate) => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed.ttsRate = rate;
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(parsed));
  } catch {}
};

const pickVoice = () => {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices?.length) return null;
  // Prefer pt-BR, then any pt
  const ptBR = voices.find((v) => /^pt[-_]BR$/i.test(v.lang));
  if (ptBR) return ptBR;
  const ptAny = voices.find((v) => /^pt/i.test(v.lang));
  return ptAny || null;
};

export const isTTSSupported = () =>
  typeof window !== "undefined" &&
  "speechSynthesis" in window &&
  "SpeechSynthesisUtterance" in window;

export default function useTTS() {
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [rate, setRateState] = useState(() => readRate());
  const queueRef = useRef([]);
  const indexRef = useRef(0);
  const supported = isTTSSupported();

  // Stop on unmount: critical to avoid TTS continuing across pages.
  useEffect(() => {
    return () => {
      if (supported) window.speechSynthesis.cancel();
    };
  }, [supported]);

  // Force voices to load on browsers that lazy-load them.
  useEffect(() => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    if (!synth.getVoices()?.length && typeof synth.onvoiceschanged !== "undefined") {
      synth.onvoiceschanged = () => {};
    }
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    queueRef.current = [];
    indexRef.current = 0;
    setPlaying(false);
    setPaused(false);
    setCurrentId(null);
  }, [supported]);

  const speakNext = useCallback(() => {
    if (!supported) return;
    const items = queueRef.current;
    const i = indexRef.current;
    if (i >= items.length) {
      setPlaying(false);
      setPaused(false);
      setCurrentId(null);
      return;
    }
    const item = items[i];
    const utt = new SpeechSynthesisUtterance(item.text);
    utt.lang = "pt-BR";
    const voice = pickVoice();
    if (voice) utt.voice = voice;
    utt.rate = rate;
    utt.onstart = () => setCurrentId(item.id);
    utt.onend = () => {
      indexRef.current += 1;
      speakNext();
    };
    utt.onerror = () => {
      indexRef.current += 1;
      speakNext();
    };
    window.speechSynthesis.speak(utt);
  }, [rate, supported]);

  const speak = useCallback((items) => {
    if (!supported) return;
    if (!Array.isArray(items) || !items.length) return;
    window.speechSynthesis.cancel();
    queueRef.current = items.map((it) => ({
      id: it.id ?? null,
      text: String(it.text || "").trim(),
    })).filter((it) => it.text);
    indexRef.current = 0;
    if (!queueRef.current.length) return;
    setPlaying(true);
    setPaused(false);
    speakNext();
  }, [speakNext, supported]);

  const pause = useCallback(() => {
    if (!supported || !playing) return;
    window.speechSynthesis.pause();
    setPaused(true);
  }, [playing, supported]);

  const resume = useCallback(() => {
    if (!supported || !paused) return;
    window.speechSynthesis.resume();
    setPaused(false);
  }, [paused, supported]);

  const restart = useCallback(() => {
    if (!supported || !queueRef.current.length) return;
    window.speechSynthesis.cancel();
    indexRef.current = 0;
    setPlaying(true);
    setPaused(false);
    speakNext();
  }, [speakNext, supported]);

  const setRate = useCallback((next) => {
    if (!ALLOWED_RATES.includes(next)) return;
    setRateState(next);
    writeRate(next);
    // Restart from current position so the new rate takes effect immediately.
    if (supported && playing) {
      window.speechSynthesis.cancel();
      // Resume from the same item (don't re-read items already finished).
      setPaused(false);
      // Schedule next tick to give cancel() time to settle on some browsers.
      setTimeout(() => speakNext(), 50);
    }
  }, [playing, speakNext, supported]);

  return {
    supported,
    playing,
    paused,
    currentId,
    rate,
    rates: ALLOWED_RATES,
    speak,
    pause,
    resume,
    stop,
    restart,
    setRate,
  };
}
